import type { PhysicalCargoSourceRow } from '../../../types/physicalCargoSource';
import { filterSelectablePhysicalSources } from '../../../features/routeCreatePhysicalSource/physicalSourceLoadingPoint';
import { resolveSuccessorWarehousePrefill } from '../../../features/routeCreatePhysicalSource/successorPhysicalSourcePrefill';

export type PendingRestoredWarehouseVerification = {
  queryApplicationId: number;
  warehouseId: number;
  stepAfterVerification: number;
};

export const RESTORED_WAREHOUSE_MISSING_MESSAGE =
  'Склад из черновика недоступен как физический источник для выбранной заявки (CP-059). Выберите источник из списка.';

export const PENDING_WAREHOUSE_VERIFICATION_NAV_MESSAGE =
  'Подождите: проверяем склад из черновика по списку физических источников CP-059…';

export function isRestoredWarehouseLoadingSource(formSlice: {
  loadingPointType?: unknown;
  loadingPointId?: unknown;
}): boolean {
  return (
    String(formSlice.loadingPointType ?? '').toLowerCase() === 'warehouse' &&
    formSlice.loadingPointId != null &&
    Number.isFinite(Number(formSlice.loadingPointId)) &&
    Number(formSlice.loadingPointId) > 0
  );
}

export function warehouseExistsInPhysicalSourcesResponse(
  sources: PhysicalCargoSourceRow[],
  warehouseId: number
): boolean {
  const selectable = filterSelectablePhysicalSources(sources);
  return selectable.some((s) => s.sourceType === 'warehouse' && s.sourceId === warehouseId);
}

/**
 * Reject async result when Application changed or response belongs to another app.
 */
export function isStalePhysicalSourcesForApplication(params: {
  queryApplicationId: number;
  currentApplicationId: number | null | undefined;
  responseApplicationId?: number | null;
}): boolean {
  const current = params.currentApplicationId != null ? Number(params.currentApplicationId) : NaN;
  if (!Number.isFinite(current) || current <= 0) return true;
  if (current !== params.queryApplicationId) return true;
  if (
    params.responseApplicationId != null &&
    Number(params.responseApplicationId) !== params.queryApplicationId
  ) {
    return true;
  }
  return false;
}

export type RestoredWarehouseVerificationOutcome =
  | { action: 'ignore_stale' }
  | { action: 'verified'; stepAfterVerification: number }
  | { action: 'rejected'; message: string };

export function evaluateRestoredWarehouseWhenPhysicalSourcesReady(params: {
  pending: PendingRestoredWarehouseVerification | null;
  currentApplicationId: number | null | undefined;
  responseApplicationId?: number | null;
  sources: PhysicalCargoSourceRow[];
}): RestoredWarehouseVerificationOutcome {
  if (!params.pending) {
    return { action: 'ignore_stale' };
  }
  if (
    isStalePhysicalSourcesForApplication({
      queryApplicationId: params.pending.queryApplicationId,
      currentApplicationId: params.currentApplicationId,
      responseApplicationId: params.responseApplicationId
    })
  ) {
    return { action: 'ignore_stale' };
  }

  const prefill = resolveSuccessorWarehousePrefill({
    applicationId: params.pending.queryApplicationId,
    warehouseId: params.pending.warehouseId,
    sources: params.sources
  });

  if (prefill.kind === 'matched') {
    return {
      action: 'verified',
      stepAfterVerification: params.pending.stepAfterVerification
    };
  }

  return {
    action: 'rejected',
    message:
      prefill.kind === 'missing' ? prefill.message : RESTORED_WAREHOUSE_MISSING_MESSAGE
  };
}

export function isPendingWarehouseVerificationForForm(params: {
  pending: PendingRestoredWarehouseVerification | null;
  formSlice: {
    applicationId?: unknown;
    loadingPointType?: unknown;
    loadingPointId?: unknown;
  };
}): boolean {
  if (!params.pending) return false;
  if (!isRestoredWarehouseLoadingSource(params.formSlice)) return false;
  const appId = Number(params.formSlice.applicationId);
  const whId = Number(params.formSlice.loadingPointId);
  return (
    Number.isFinite(appId) &&
    appId === params.pending.queryApplicationId &&
    whId === params.pending.warehouseId
  );
}

/** Blocks navigation to Cargo (step 4) until warehouse is confirmed in physical-sources. */
export function evaluateLegacyWarehouseCargoStepAccess(params: {
  groupUiEnabled: boolean;
  formSlice: {
    applicationId?: unknown;
    loadingPointType?: unknown;
    loadingPointId?: unknown;
  };
  pendingWarehouseVerification: PendingRestoredWarehouseVerification | null;
  confirmedSourceKey: string | null;
}): { ok: true } | { ok: false; message: string } {
  if (params.groupUiEnabled) return { ok: true };
  if (!isRestoredWarehouseLoadingSource(params.formSlice)) return { ok: true };

  if (
    isPendingWarehouseVerificationForForm({
      pending: params.pendingWarehouseVerification,
      formSlice: params.formSlice
    })
  ) {
    return { ok: false, message: PENDING_WAREHOUSE_VERIFICATION_NAV_MESSAGE };
  }

  const expectedKey = `${String(params.formSlice.loadingPointType ?? '')}|${String(
    params.formSlice.loadingPointId ?? ''
  )}`;
  if (
    !legacyWarehouseCargoQueryAllowed({
      groupUiEnabled: false,
      loadingPointType: params.formSlice.loadingPointType,
      loadingPointId: params.formSlice.loadingPointId,
      applicationId: params.formSlice.applicationId,
      pendingWarehouseVerification: params.pendingWarehouseVerification,
      confirmedSourceKey: params.confirmedSourceKey,
      expectedSourceKey: expectedKey
    })
  ) {
    return { ok: false, message: RESTORED_WAREHOUSE_MISSING_MESSAGE };
  }
  return { ok: true };
}

/** Whether GET /routes/cargo/available may run for legacy warehouse source. */
export function legacyWarehouseCargoQueryAllowed(params: {
  groupUiEnabled: boolean;
  loadingPointType?: unknown;
  loadingPointId?: unknown;
  applicationId?: unknown;
  pendingWarehouseVerification?: PendingRestoredWarehouseVerification | null;
  confirmedSourceKey: string | null;
  expectedSourceKey: string | null;
}): boolean {
  if (params.groupUiEnabled) return true;
  if (String(params.loadingPointType ?? '').toLowerCase() !== 'warehouse') return true;
  if (
    isPendingWarehouseVerificationForForm({
      pending: params.pendingWarehouseVerification ?? null,
      formSlice: {
        applicationId: params.applicationId,
        loadingPointType: params.loadingPointType,
        loadingPointId: params.loadingPointId
      }
    })
  ) {
    return false;
  }
  if (!params.expectedSourceKey) return false;
  return params.confirmedSourceKey === params.expectedSourceKey;
}
